import { AppLogo } from "@/components/AppLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/AuthContext";
import { loginAdmin } from "@/services/auth.service";
import { Lock, Mail } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await loginAdmin(email, password);

      await login(response.token);

      if (response.role === "SUPERUSER") {
        navigate("/superuser/dashboard");
      } else if (response.role === "ADMIN") {
        navigate("/admin/dashboard");
      } else {
        navigate("/");
      }
    } catch (error: unknown) {
      setError(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Panneau marque — couleur primary du thème */}
      <aside className="flex flex-col items-center justify-center bg-primary px-8 py-14 text-primary-foreground md:py-12">
        <div className="flex max-w-md flex-col items-center gap-5 text-center">
          <div className="flex w-full flex-col items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <AppLogo size="lg" className="shrink-0 shadow-lg" />
              <h1 className="text-3xl tracking-tight sm:text-4xl">
                <span className="font-display font-normal">Pointo</span>
              </h1>
            </div>
            <div>
              <p className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                Espace administrateur
              </p>
              <p className="mt-4 text-base leading-relaxed text-white/90 sm:text-lg">
                Gérez vos employés et leurs plannings en toute simplicité
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Formulaire */}
      <div className="flex flex-1 items-center justify-center bg-gray-50 px-4 py-12 md:px-8">
        <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-8 shadow-xl sm:p-10">
          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">
              Connexion
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Accédez à votre espace administrateur
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-sm font-semibold text-gray-900"
              >
                Email
              </Label>
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-11 border-gray-200 bg-gray-50 pl-10 text-gray-900 placeholder:text-muted-foreground focus-visible:bg-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-sm font-semibold text-gray-900"
              >
                Mot de passe
              </Label>
              <div className="relative">
                <Lock
                  className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-11 border-gray-200 bg-gray-50 pl-10 text-gray-900 placeholder:text-muted-foreground focus-visible:bg-white"
                />
              </div>
            </div>

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              className="h-11 w-full font-semibold shadow-sm transition-shadow hover:shadow-md"
              disabled={isLoading}
            >
              {isLoading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
